import { useState, useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Sparkles } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { PaymentService } from '@/supabase/payment/paymentService';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ParsingOptions } from '@/hooks/useParsingOptions';
import { Checkbox } from "@/components/ui/checkbox"
import { isEmptyFieldValue, formatPhoneNumber } from '@/lib/utils';
import { logUsage } from '@/lib/usageLog'


export interface ShippingInfo {
  customer_name: string;
  customer_phone: string;
  customer_address: string;
  delivery_notes: string;
  nickname: string;
  productName: string;
}

interface ShippingInfoFormProps {
  initialData?: Partial<ShippingInfo>;
  onSave: (data: ShippingInfo) => void;
  onClose: () => void;
  /**
   * 폼이 dirty(수동 필드 입력 또는 자동 파싱 시작)해지는 순간 호출.
   * 매니저가 늦게 도착한 서버 값/자동완성으로 사용자 입력을 덮지 않기 위한 동기 신호.
   * 고객 답글 textarea는 배송 필드가 아니므로 dirty로 치지 않는다.
   */
  onDirty?: () => void;
  parsingOptions: ParsingOptions;
  setParsingOptions: (options: Partial<ParsingOptions>) => void;
  wasAutofilled?: boolean;
  /** 저장 요청 in-flight 여부. 저장 버튼만 비활성화하고 닫기 경로는 그대로 둔다. */
  saving: boolean;
}

// initialData의 "string"이나 "null" 같은 의미 없는 값을 빈 문자열로 정리한 폼 값.
// 초기 state와 initialData 동기화 effect가 같은 정규화를 공유해야 마운트 직후 effect가
// 동일 값 재설정(추가 렌더 1회)을 만들지 않는다.
const cleanFormData = (data?: Partial<ShippingInfo>): ShippingInfo => {
  const getCleanValue = (value: string | undefined | null): string => {
    return isEmptyFieldValue(value) ? '' : value!;
  };
  return {
    customer_name: getCleanValue(data?.customer_name),
    customer_phone: getCleanValue(data?.customer_phone),
    customer_address: getCleanValue(data?.customer_address),
    delivery_notes: getCleanValue(data?.delivery_notes),
    nickname: getCleanValue(data?.nickname),
    productName: getCleanValue(data?.productName),
  };
};

export const ShippingInfoForm = ({ initialData, onSave, onClose, onDirty, parsingOptions, setParsingOptions, wasAutofilled, saving }: ShippingInfoFormProps) => {
  const [formData, setFormData] = useState<ShippingInfo>(() => cleanFormData(initialData));
  const [customerReply, setCustomerReply] = useState('');
  const [isParsing, setIsParsing] = useState(false);
  const { user } = useAuth();
  const [paymentService] = useState(() => new PaymentService());
  const [showUsageLimitDialog, setShowUsageLimitDialog] = useState(false);

  useEffect(() => {
    // initialData prop이 변경될 때마다 폼의 내부 상태를 동기화합니다.
    // 마운트 직후 첫 실행은 initializer와 동일한 정규화 결과 → prev 반환으로 렌더 없음.
    setFormData((prev) => {
      const next = cleanFormData(initialData);
      return (
        next.customer_name === prev.customer_name &&
        next.customer_phone === prev.customer_phone &&
        next.customer_address === prev.customer_address &&
        next.delivery_notes === prev.delivery_notes &&
        next.nickname === prev.nickname &&
        next.productName === prev.productName
      ) ? prev : next;
    });
  }, [initialData]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    onDirty?.();
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAutoFill = useCallback(async () => {
    if (!customerReply.trim()) {
      // 나중에 toast 라이브러리로 교체 가능
      alert('고객 답장을 먼저 붙여넣어 주세요.');
      return;
    }

    // 파싱 시작 시점부터 dirty — 파싱 완료 전에 늦은 서버 값이 폼을 덮지 못하게 한다.
    onDirty?.();

    // 회원 사용량 증가 체크 (ShippingInfoForm은 로그인 사용자 전용이지만 안전하게 처리)
    if (user?.id) {
      try {
        const usageResult = await paymentService.incrementUsage(user.id);
        if (!usageResult.success) {
          if (usageResult.reason === 'usage_limit_exceeded') {
            setShowUsageLimitDialog(true);
            return; // 한도 초과 시 더 진행하지 않음
          }
          // 기타 실패 사유 안내 (필요 시 UI 개선)
          alert(usageResult.reason === 'no_active_subscription'
            ? '구독이 활성화되어 있지 않습니다. 마이페이지에서 구독을 확인해주세요.'
            : '사용량 처리 중 오류가 발생했습니다.');
          return;
        }
      } catch (e) {
        console.error('사용량 증가 중 오류:', e);
        alert('사용량 처리 중 오류가 발생했습니다.');
        return;
      }
    }

    setIsParsing(true);
    try {
      const response = await fetch("https://gsvtnnoutinzsbasjvoz.supabase.co/functions/v1/parse-reply", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImdzdnRubm91dGluenNiYXNqdm96Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDAwNjA3ODMsImV4cCI6MjA1NTYzNjc4M30.nc2HXlY8_dKhPbBbem6nCmaUv_cDe8jsuSh-D13ijhI`
        },
        body: JSON.stringify({ input: customerReply, options: parsingOptions })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "파싱에 실패했습니다.");
      }

      const result = await response.json();
      const parsedData = result.output;

      const getCleanValue = (value: string | undefined | null): string => {
        return isEmptyFieldValue(value) ? '' : value!;
      };

      // Enforce nickname from parent (CustomerTemplateManager) regardless of parse option
      const enforcedNickname = getCleanValue(initialData?.nickname ?? formData.nickname);

      // If product name extraction is enabled, prefer settlement data's product list
      const productNameFromSettlement = getCleanValue(initialData?.productName ?? formData.productName);
      const finalProductName = parsingOptions.parseProductName
        ? productNameFromSettlement
        : getCleanValue(parsedData.productName);

      const updatedFormData: Partial<ShippingInfo> = {
        customer_name: getCleanValue(parsedData.name),
        customer_phone: formatPhoneNumber(getCleanValue(parsedData.phone)),
        customer_address: getCleanValue(parsedData.address),
        delivery_notes: getCleanValue(parsedData.deliveryNotes),
        nickname: enforcedNickname,
        productName: finalProductName,
      };

      setFormData(prev => ({ ...prev, ...updatedFormData }));

      // 사용 로그 기록 (회원만, 정산서 화면에서의 parsing → order-parse)
      setTimeout(() => {
        if (user?.id) {
          void logUsage({
            userId: user.id,
            pseudoUserId: '',
            isMember: true,
            type: 'order-parse'
          });
        }
      }, 0);

    } catch (error) {
      console.error('Failed to parse customer reply:', error);
      alert('자동 정리하기에 실패했습니다.');
    } finally {
      setIsParsing(false);
    }
  }, [customerReply, user?.id, paymentService, parsingOptions, initialData, formData.nickname, formData.productName, onDirty]);

  const handleSaveClick = () => {
    onSave(formData);
  };

  return (
    <div className="space-y-6 p-1">
      <div>
        <div className="flex items-center gap-2 mb-2">
          <span className="inline-flex items-center px-1.5 py-0.5 text-[11px] rounded bg-teal-50 text-teal-700 border border-teal-100">STEP 1</span>
          <h3 className="text-base font-semibold text-gray-800">고객 답장으로 자동 정리</h3>
        </div>
        <div className="space-y-3">
          <Textarea
            placeholder="고객으로부터 받은 답장을 여기에 그대로 붙여넣어보세요."
            value={customerReply}
            onChange={(e) => setCustomerReply(e.target.value)}
            className="min-h-[100px]"
          />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 text-sm">
              <Label htmlFor="parseNickname" className="flex items-center gap-2 cursor-pointer">
                <Checkbox id="parseNickname" checked={parsingOptions.parseNickname} onCheckedChange={(checked) => setParsingOptions({ parseNickname: !!checked })} />
                닉네임 추출
              </Label>
              <Label htmlFor="parseProductName" className="flex items-center gap-2 cursor-pointer">
                <Checkbox id="parseProductName" checked={parsingOptions.parseProductName} onCheckedChange={(checked) => setParsingOptions({ parseProductName: !!checked })} />
                상품명 추출
              </Label>
            </div>
            <Button onClick={handleAutoFill} disabled={isParsing} className="bg-teal-500 hover:bg-teal-600 text-white">
              {isParsing ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="mr-2 h-4 w-4" />
              )}
              자동 정리하기
            </Button>
          </div>
          <p className="mt-2 text-[11px] text-gray-500">
            자동 정리하기로 추출한 내용은 저장 시 ‘배송 엑셀’ 페이지에 자동으로 추가됩니다.
          </p>
        </div>
      </div>

      <div className="border-t pt-6">
        <div className="flex items-center gap-2 mb-4">
          <span className="inline-flex items-center px-1.5 py-0.5 text-[11px] rounded bg-gray-50 text-gray-700 border border-gray-200">STEP 2</span>
          <h3 className="text-base font-medium text-gray-700">배송 정보 수동 보완</h3>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {parsingOptions.parseNickname && (
            <div className="space-y-2">
              <Label htmlFor="nickname">닉네임</Label>
              <Input
                id="nickname"
                name="nickname"
                value={formData.nickname}
                onChange={handleInputChange}
              />
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="customer_name" className="flex items-center gap-2">
              <span>이름</span>
              {wasAutofilled && formData.customer_name && (
                <span className="text-[11px] font-semibold text-indigo-700 bg-indigo-100 border border-indigo-200 rounded px-1.5 py-0.5">
                  자동 채움
                </span>
              )}
            </Label>
            <Input
              id="customer_name"
              name="customer_name"
              value={formData.customer_name}
              onChange={handleInputChange}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="customer_phone" className="flex items-center gap-2">
              <span>전화번호</span>
              {wasAutofilled && formData.customer_phone && (
                <span className="text-[11px] font-semibold text-indigo-700 bg-indigo-100 border border-indigo-200 rounded px-1.5 py-0.5">
                  자동 채움
                </span>
              )}
            </Label>
            <Input
              id="customer_phone"
              name="customer_phone"
              value={formData.customer_phone}
              onChange={handleInputChange}
            />
          </div>
          <div className="sm:col-span-2 space-y-2">
            <Label htmlFor="customer_address" className="flex items-center gap-2">
              <span>주소</span>
              {wasAutofilled && formData.customer_address && (
                <span className="text-[11px] font-semibold text-indigo-700 bg-indigo-100 border border-indigo-200 rounded px-1.5 py-0.5">
                  자동 채움
                </span>
              )}
            </Label>
            <Input
              id="customer_address"
              name="customer_address"
              value={formData.customer_address}
              onChange={handleInputChange}
            />
          </div>
          {parsingOptions.parseProductName && (
            <div className="sm:col-span-2 space-y-2">
              <Label htmlFor="productName">상품명</Label>
              <Input
                id="productName"
                name="productName"
                value={formData.productName}
                onChange={handleInputChange}
              />
            </div>
          )}
          <div className="sm:col-span-2 space-y-2">
            <Label htmlFor="delivery_notes">배송 메모</Label>
            <Input
              id="delivery_notes"
              name="delivery_notes"
              value={formData.delivery_notes}
              onChange={handleInputChange}
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button variant="ghost" onClick={onClose}>
          취소
        </Button>
        <Button onClick={handleSaveClick} disabled={saving}>{saving ? '저장 중' : '저장'}</Button>
      </div>

      {/* 사용 한도 도달 다이얼로그 */}
      <AlertDialog open={showUsageLimitDialog} onOpenChange={setShowUsageLimitDialog}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader className="space-y-3">
            <AlertDialogTitle className="text-xl font-semibold text-gray-900">이번 달 사용 한도 도달</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4 text-base">
                <p className="text-gray-700">현재 구독 플랜의 이번 달 사용 가능한 횟수를 모두 소진하셨습니다.</p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">
                    • 스탠다드 플랜: 월 150건 사용 가능
                    <br />
                    • 프리미엄 플랜: 무제한 사용 가능
                  </p>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className="bg-gray-100 hover:bg-gray-200 text-gray-700">닫기</AlertDialogCancel>
            <AlertDialogAction
              className="bg-teal-500 hover:bg-teal-600 text-white"
              onClick={() => {
                setShowUsageLimitDialog(false);
                window.location.href = '/mypage';
              }}
            >
              플랜 변경하기
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};